import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import {
  adjustCustomerStamps,
  deactivateLoyaltyProgram,
  redeemVoucherCredit,
  redeemVoucherDiscount,
  updateLoyaltyConfig,
  voidClaimedVoucher,
  voidVoucher,
} from "hooks/api/loyalty";
import { translate } from "shared/languages";
import { invalidateCustomerList } from "hooks/mutations/customerList";
import { getApiErrorMessage, getApiSuccessMessage } from "shared/utils";

export const useUpdateLoyaltyConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateLoyaltyConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      getApiSuccessMessage(translate("loyalty.toast.config_saved"));
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useDeactivateLoyaltyProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deactivateLoyaltyProgram,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      getApiSuccessMessage(translate("loyalty.toast.deactivated"));
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useAdjustCustomerStamps = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adjustCustomerStamps,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["loyalty", "customer", variables.customerId],
      });
      getApiSuccessMessage(translate("loyalty.toast.stamps_updated"));
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// Redemptions create a customer_package, so the package namespace refetches
// alongside the loyalty view (same invalidation the buy modal uses).
export const useRedeemVoucherCredit = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: redeemVoucherCredit,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      // Referral-sourced vouchers redeem through these same endpoints; their
      // surfaces refetch too.
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      queryClient.invalidateQueries({ queryKey: ["customer_package"] });
      invalidateCustomerList(queryClient);
      getApiSuccessMessage(translate("loyalty.toast.credit_redeemed"));
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useRedeemVoucherDiscount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: redeemVoucherDiscount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      // Referral-sourced vouchers redeem through these same endpoints; their
      // surfaces refetch too.
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      queryClient.invalidateQueries({ queryKey: ["customer_package"] });
      invalidateCustomerList(queryClient);
      getApiSuccessMessage(translate("loyalty.toast.discount_redeemed"));
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

export const useVoidVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: voidVoucher,
    // The server reports whether the stamp card actually rolled back
    // (structured `rolled_back`, not prose, so the toast stays localized). It
    // says no when the reward was never stamp-backed (referral) or when a
    // newer stamp has already settled the card.
    onSuccess: (data?: { rolled_back?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      getApiSuccessMessage(
        translate(
          data?.rolled_back
            ? "loyalty.toast.voucher_voided_rolled_back"
            : "loyalty.toast.voucher_voided"
        )
      );
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};

// The claw-back invalidates the reward customer_package too (the credit
// disappears from the client's Paket tab), unlike the plain issued-voucher
// void, which never created one.
export const useVoidClaimedVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: voidClaimedVoucher,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      // Referral-sourced vouchers redeem through these same endpoints; their
      // surfaces refetch too.
      queryClient.invalidateQueries({ queryKey: ["referral"] });
      queryClient.invalidateQueries({ queryKey: ["customer_package"] });
      invalidateCustomerList(queryClient);
      getApiSuccessMessage(translate("loyalty.toast.claimed_voided"));
    },
    onError: (error: AxiosError<ErrorResponse>) => getApiErrorMessage(error),
  });
};
